import React, { cloneElement, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import '../styles/notificationDropdown.css';

interface Props {
    open: boolean;
    anchorRef: React.RefObject<HTMLElement>;
    onClose: () => void;
    className?: string;
    children: React.ReactElement<{ style?: React.CSSProperties }>;
}

export default function NotificationPopover({ open, anchorRef, onClose, className, children }: Props) {
    const [position, setPosition] = useState<React.CSSProperties | null>(null);

    useLayoutEffect(() => {
        if (!open) { setPosition(null); return; }
        const update = () => {
            const anchor = anchorRef.current;
            if (!anchor) return;
            const viewport = window.visualViewport;
            const viewportWidth = viewport?.width ?? window.innerWidth;
            const viewportHeight = viewport?.height ?? window.innerHeight;
            const offsetLeft = viewport?.offsetLeft ?? 0;
            const offsetTop = viewport?.offsetTop ?? 0;
            const margin = Math.min(12, viewportWidth / 4);
            const rect = anchor.getBoundingClientRect();
            const width = Math.min(viewportWidth <= 640 ? viewportWidth - 2 * margin : 360, viewportWidth - 2 * margin);
            const left = viewportWidth <= 640 ? offsetLeft + margin :
                Math.max(offsetLeft + margin, Math.min(rect.right - width, offsetLeft + viewportWidth - width - margin));
            const bottom = offsetTop + viewportHeight - margin;
            const below = bottom - rect.bottom - 8;
            const above = rect.top - offsetTop - margin - 8;
            const placeAbove = below < 180 && above > below;
            const top = placeAbove ?
                Math.max(offsetTop + margin, rect.top - 8 - Math.min(460, above)) :
                Math.max(offsetTop + margin, Math.min(rect.bottom + 8, bottom - 100));
            setPosition({ position: 'fixed', left, top, right: 'auto', width,
                maxHeight: Math.max(0, (placeAbove ? Math.min(rect.top - 8, bottom) : bottom) - top), zIndex: 10000 });
        };
        update();
        window.addEventListener('resize', update);
        window.addEventListener('scroll', update, true);
        window.visualViewport?.addEventListener('resize', update);
        window.visualViewport?.addEventListener('scroll', update);
        const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
        if (anchorRef.current) observer?.observe(anchorRef.current);
        return () => {
            window.removeEventListener('resize', update);
            window.removeEventListener('scroll', update, true);
            window.visualViewport?.removeEventListener('resize', update);
            window.visualViewport?.removeEventListener('scroll', update);
            observer?.disconnect();
        };
    }, [open, anchorRef]);

    useLayoutEffect(() => {
        if (!open) return;
        const escape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
                anchorRef.current?.querySelector('button')?.focus();
            }
        };
        document.addEventListener('keydown', escape);
        return () => document.removeEventListener('keydown', escape);
    }, [open, anchorRef, onClose]);

    if (!open || !position) return null;
    return createPortal(<div className={className}>
        {cloneElement(children, { style: { ...children.props.style, ...position } })}
    </div>, document.body);
}
